import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import SearchPage from './components/SearchPage';
import MatchesPage from './components/MatchesPage';
import SettingsPage from './components/SettingsPage';
import NotificationToast from './components/NotificationToast';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import AdminPage from './components/AdminPage';
import VerifyEmailPage from './components/VerifyEmailPage';

// Keep this component outside of App to avoid remounting children on App re-renders
const RequireAuth = ({ children, authToken }) => {
  const location = useLocation();
  if (!authToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

function App() {
  const [notification, setNotification] = useState(null);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('auth_token') || null);
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('auth_user') || 'null'); } catch { return null; }
  });
  const [searchStatus, setSearchStatus] = useState({
    is_running: false,
    current_search: '',
    progress: 0,
    matches_found: 0,
    total_images_processed: 0
  });

  const showNotification = useCallback((message, type = 'info', duration = 3000) => {
    setNotification({ message, type, id: Date.now() });
    setTimeout(() => {
      setNotification(null);
    }, duration);
  }, []);

  const handleSetSearchStatus = useCallback((status) => {
    setSearchStatus(status);
  }, []);

  const handleLogin = useCallback((token, user) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user || null));
    setAuthToken(token);
    setCurrentUser(user || null);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setAuthToken(null);
    setCurrentUser(null);
  }, []);

  // (Removed inline RequireAuth to prevent identity changes across renders)

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Navbar onLogout={handleLogout} isAuthenticated={!!authToken} user={currentUser} />
        
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route 
              path="/" 
              element={
                <Dashboard 
                  searchStatus={searchStatus}
                  showNotification={showNotification}
                />
              } 
            />
            <Route 
              path="/login" 
              element={
                <LoginPage 
                  showNotification={showNotification}
                  onLogin={handleLogin}
                />
              } 
            />
            <Route 
              path="/register" 
              element={
                <RegisterPage 
                  showNotification={showNotification}
                />
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                <ForgotPasswordPage 
                  showNotification={showNotification}
                />
              } 
            />
            <Route 
              path="/verify-email" 
              element={
                <VerifyEmailPage 
                  showNotification={showNotification}
                />
              } 
            />
            <Route 
              path="/search" 
              element={
                <RequireAuth authToken={authToken}>
                  <SearchPage 
                    searchStatus={searchStatus}
                    setSearchStatus={handleSetSearchStatus}
                    showNotification={showNotification}
                  />
                </RequireAuth>
              } 
            />
            <Route 
              path="/matches" 
              element={
                <RequireAuth authToken={authToken}>
                  <MatchesPage 
                    showNotification={showNotification}
                  />
                </RequireAuth>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <RequireAuth authToken={authToken}>
                  <AdminPage 
                    showNotification={showNotification}
                  />
                </RequireAuth>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <RequireAuth authToken={authToken}>
                  <SettingsPage 
                    showNotification={showNotification}
                  />
                </RequireAuth>
              } 
            />
          </Routes>
        </main>

        {notification && (
          <NotificationToast
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </Router>
  );
}

export default App;

